import { inject } from '@angular/core';
import { assert, isDefined } from '@sama/js-core';
import { DirectiveSuperclass } from '@sama/ng-core';
import { filter, Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { assertStore } from '../../../../../../cross-team-shared/misc-utils';
import { ToolName } from '../../../../../../cross-team-shared/tools-names';
import { getOutputByName } from '../../../../../h3ml/dsl/sensor-fusion/sensor-fusion-output.utils';
import { TaskWorkspaceStore } from '../../../../../h3ml/state/task-workspace-store';
import { getSelectedToolStore } from '../../../../../h3ml/state/task-workspace-store-utils';
import { RootKeyboardShortcutService } from '../../../../../shared/keyboard-shortcuts/root-keyboard-shortcut.service';
import { FusionManagerFactory } from '../../fusion/fusion-manager-factory.service';

export abstract class AbstractToolModeSecondaryToolbar extends DirectiveSuperclass {
  selectedToolMode$: Observable<string>;

  protected taskWorkspaceStore = inject(TaskWorkspaceStore);
  protected keyboardShortcutService = inject(RootKeyboardShortcutService);
  private fusionManagerFactory = inject(FusionManagerFactory);

  protected abstract toolName: ToolName;

  constructor() {
    super();
    const { selectedOutputName } = this.taskWorkspaceStore.state();
    this.selectedToolMode$ = getSelectedToolStore(
      this.taskWorkspaceStore,
      selectedOutputName,
    ).$.pipe(
      map((selectedTool) => selectedTool?.mode),
      filter(isDefined),
    );
  }

  setMode(mode: string) {
    this.getContentConnector().setTool(this.toolName, mode);
  }

  protected cycleToNextMode(orderedModes: string[]) {
    const { selectedOutputName } = this.taskWorkspaceStore.state();
    const toolStore = getSelectedToolStore(
      this.taskWorkspaceStore,
      selectedOutputName,
    );
    assertStore(toolStore, 'Tool store should always be defined');
    const currentMode = toolStore.state().mode;
    if (!currentMode) {
      return;
    }

    const currentIndex = orderedModes.indexOf(currentMode);
    assert(currentIndex !== -1, 'Current mode not found in ordered modes');
    this.setMode(orderedModes[(currentIndex + 1) % orderedModes.length]);
  }

  // ContentConnector changes across task, always getting it on use ensures we
  // have the correct reference
  private getContentConnector() {
    const { selectedOutputName, project } = this.taskWorkspaceStore.state();
    const outputId = getOutputByName(project, selectedOutputName).id;
    return this.fusionManagerFactory
      .getManager(outputId)
      .getContentConnector(selectedOutputName);
  }
}
